import { useState, useEffect, useRef } from 'react';
import {
  generateRegulationAsync,
  getRegulationJobStatus,
  parseRegulation,
  RegulationGenerationRequest,
  RegulationJobStatus,
  RegulationResponse,
} from '../services/regulationApi';

interface UseRegulationGenerationAsyncProps {
  restorePolicyData?: Record<string, any>;
  savedFormData?: Record<string, string>;
  skipAutoGeneration?: boolean;
  pollIntervalMs?: number;
  maxPollTimeMs?: number;
  searchQuery?: string;
  maxSearchResults?: number;
  tenantId?: string;
}

interface UseRegulationGenerationAsyncResult {
  isLoading: boolean;
  formData: Record<string, string>;
  hasGenerated: boolean;
  error: string | null;
  jobStatus: RegulationJobStatus | null;
  elapsedSeconds: number;
  regenerate: () => Promise<void>;
}

export const useRegulationGenerationAsync = ({
  restorePolicyData,
  savedFormData,
  skipAutoGeneration,
  pollIntervalMs = 2000,
  maxPollTimeMs = 300000,
  searchQuery,
  maxSearchResults = 5,
  tenantId,
}: UseRegulationGenerationAsyncProps): UseRegulationGenerationAsyncResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>(() => savedFormData ?? {});
  const [hasGenerated, setHasGenerated] = useState(() => Boolean(savedFormData));
  const [error, setError] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<RegulationJobStatus | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  // savedFormDataがある場合は既に生成済みとみなす
  const hasExecutedRef = useRef(Boolean(savedFormData));

  // ポーリングクリーンアップ
  const cleanupPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
  };

  // ジョブステータスをポーリング
  const pollJobStatus = async (jobId: string) => {
    startTimeRef.current = Date.now();
    setElapsedSeconds(0);

    // 経過時間更新用のインターバル
    elapsedIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsedSeconds(elapsed);
    }, 1000);

    // ポーリング開始
    pollIntervalRef.current = setInterval(async () => {
      try {
        const jobResponse = await getRegulationJobStatus(jobId, tenantId);
        setJobStatus(jobResponse.status);

        // 完了または失敗の場合、ポーリング停止
        if (jobResponse.status === 'completed' || jobResponse.status === 'failed') {
          cleanupPolling();
          setIsLoading(false);

          if (jobResponse.status === 'completed' && jobResponse.result) {
            // 結果を処理
            const result = jobResponse.result;

            // ドキュメントをparseRegulation関数で各セクションに展開
            const parsedSections = parseRegulation(result.regulation_document);

            setFormData(parsedSections);
            setHasGenerated(true);
            setError(null);
          } else if (jobResponse.status === 'failed') {
            setError(jobResponse.error || 'BCP規程の生成に失敗しました');
          }
        }

        // 最大ポーリング時間を超えた場合
        if (Date.now() - startTimeRef.current > maxPollTimeMs) {
          cleanupPolling();
          setIsLoading(false);
          setError('タイムアウト: BCP規程の生成に時間がかかっています');
        }
      } catch (err: any) {
        cleanupPolling();
        setIsLoading(false);
        setError(err.message || 'ジョブステータスの取得中にエラーが発生しました');
      }
    }, pollIntervalMs);
  };

  // BCP規程を生成(非同期)
  const generate = async () => {
    if (hasExecutedRef.current || isLoading) {
      return;
    }

    hasExecutedRef.current = true;
    setIsLoading(true);
    setError(null);
    setJobStatus(null);
    setElapsedSeconds(0);

    try {
      if (!restorePolicyData) {
        throw new Error('復旧方針データが必要です');
      }

      // 企業情報を復旧方針データから抽出
      // company_infoは文字列フィールドとしてrestorePolicyDataのトップレベルに存在
      const companyInfoText = restorePolicyData.company_info || '';

      // company_info文字列から情報を抽出
      const extractFromText = (text: string, label: string): string => {
        const regex = new RegExp(`${label}[：:]\s*([^\n]+)`);
        const match = text.match(regex);
        return match ? match[1].trim() : '';
      };

      const companyName = extractFromText(companyInfoText, '企業名');
      const businessContent = extractFromText(companyInfoText, '事業内容');
      const location = extractFromText(companyInfoText, '所在地') || '東京都';
      // Support both new field name and legacy field name for backward compatibility
      const mainBusiness = restorePolicyData.restore_policy_important_business || restorePolicyData.important_business || '';

      const finalSearchQuery = searchQuery || `${businessContent || 'サービス業'} BCP規程 ${location}`;

      const request: RegulationGenerationRequest = {
        company_name: companyName,
        business_type: businessContent || 'サービス業',
        location: location,
        main_business: mainBusiness,
        employee_count: undefined,
        annual_revenue: undefined,
        restore_policy_data: restorePolicyData,
        use_parallel_generation: true,
        use_section_based_generation: true,
        search_query: finalSearchQuery,
        max_search_results: maxSearchResults,
        tenant_id: tenantId,
      };

      // 非同期ジョブ作成
      const { job_id } = await generateRegulationAsync(request);
      setJobStatus('pending');

      // ポーリング開始
      await pollJobStatus(job_id);
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'BCP規程の生成中にエラーが発生しました');
    }
  };

  // 再生成
  const regenerate = async () => {
    hasExecutedRef.current = false;
    setHasGenerated(false);
    await generate();
  };

  // アンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      cleanupPolling();
    };
  }, []);

  // 自動生成(初回のみ)
  useEffect(() => {
    // skipAutoGenerationがtrueの場合は何もしない
    if (skipAutoGeneration) {
      return;
    }

    // 既に実行済みの場合は何もしない
    if (hasExecutedRef.current) {
      return;
    }

    // restorePolicyDataがない場合は何もしない
    if (!restorePolicyData) {
      return;
    }

    // 上記条件をすべてクリアした場合のみ生成
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restorePolicyData, skipAutoGeneration]);

  return {
    isLoading,
    formData,
    hasGenerated,
    error,
    jobStatus,
    elapsedSeconds,
    regenerate,
  };
};
